import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Box,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AxiosError } from 'axios';

import { fetchStrategyLogs, QueueStrategy } from '../../../api/queueStrategies';

interface AuditLogDialogProps {
  open: boolean;
  strategy: QueueStrategy | null;
  onClose: () => void;
}

interface StrategyLogItem {
  id: string;
  strategyId: string;
  queueId?: string;
  queueName?: string;
  ticketId?: string;
  ticketNumber?: string;
  userId?: string;
  username?: string;
  action: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

const AuditLogDialog: React.FC<AuditLogDialogProps> = ({ open, strategy, onClose }) => {
  const [logs, setLogs] = useState<StrategyLogItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedLogIds, setExpandedLogIds] = useState<Record<string, boolean>>({});

  const loadLogs = useCallback(async () => {
    if (!strategy) {
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchStrategyLogs(strategy.id, 100);
      setLogs(data);
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      const message = axiosError.response?.data?.message || axiosError.message || 'Не удалось загрузить журнал';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [strategy]);

  useEffect(() => {
    if (open && strategy) {
      loadLogs();
    }
  }, [loadLogs, open, strategy]);

  const toggleLog = (id: string) => {
    setExpandedLogIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleClose = () => {
    if (isLoading) {
      return;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Аудит стратегии {strategy?.name}</Typography>
        <Tooltip title="Обновить">
          <span>
            <LoadingButton
              onClick={loadLogs}
              loading={isLoading}
              loadingPosition="center"
              variant="outlined"
              size="small"
            >
              <RefreshIcon fontSize="small" />
            </LoadingButton>
          </span>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Отображаются последние события изменений стратегии, действий пользователей и результатов dry-run.
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {!isLoading && logs.length === 0 && !error && (
            <Alert severity="info">Пока нет записей аудита для этой стратегии.</Alert>
          )}

          <List disablePadding>
            {logs.map((log) => {
              const isExpanded = expandedLogIds[log.id] ?? false;
              return (
                <Box key={log.id} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                  <ListItem
                    secondaryAction={
                      <IconButton edge="end" onClick={() => toggleLog(log.id)} size="small">
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="subtitle2">{log.action}</Typography>
                          {log.queueName && <Chip label={log.queueName} size="small" variant="outlined" />}
                          {log.ticketNumber && <Chip label={`Талон ${log.ticketNumber}`} size="small" />}
                          <Typography variant="caption" color="text.secondary">
                            {new Date(log.createdAt).toLocaleString('ru-RU')}
                          </Typography>
                        </Stack>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {log.username ? `Пользователь: ${log.username}` : 'Система'}
                        </Typography>
                      }
                    />
                  </ListItem>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 3, pb: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Детали события
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          backgroundColor: 'grey.100',
                          borderRadius: 1,
                          p: 2,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {JSON.stringify(log.payload ?? {}, null, 2)}
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </List>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Закрыть
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditLogDialog;
